import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { Button } from "./button";
import { Checkbox } from "./checkbox";

export type DropdownOption = {
  label: string | React.ReactNode;
  value?: string;
  checked?: boolean;
  disabled?: boolean;
};
export type DropdownRouteOption = {
  label: string;
  route: string;
};
export type DropdownHrefOption = {
  label: string | React.ReactNode;
  href: string;
  target?: string;
};

export function Dropdown({
  label,
  options,
  className,
  buttonText = false,
  buttonSize = "md",
  buttonSeverity = "primary",
  disabled = false,
  asCheckbox = false,
  showSelectAll = false,
  listClassName,
  itemClassName,
  onSelectAll,
  onSelect,
}: {
  label?: string | React.ReactNode;
  options: (DropdownOption | DropdownRouteOption | DropdownHrefOption)[];
  className?: string;
  buttonText?: boolean;
  buttonSize?: "sm" | "md" | "lg";
  buttonSeverity?: "primary" | "secondary" | "danger";
  disabled?: boolean;
  showSelectAll?: boolean;
  asCheckbox?: boolean;
  listClassName?: string;
  itemClassName?: string;
  onSelectAll?: (value: boolean) => void;
  onSelect?: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className={`relative h-full whitespace-nowrap ${className}`}>
      <Button
        disabled={disabled}
        severity={buttonSeverity}
        size={buttonSize}
        text={buttonText}
        type="button"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-2 h-[16px]">
          {label}
          <FontAwesomeIcon icon={faChevronDown} />
        </div>
      </Button>
      {open && (
        <ul
          className={`absolute z-10 top-[calc(100%+1px)] right-0 min-w-full ${listClassName}`}
        >
          {showSelectAll && (
            <li className={`px-2 py-1 text-left ${itemClassName}`}>
              <Checkbox
                checked={options.every(
                  (option) => "checked" in option && option.checked,
                )}
                className="w-full block"
                label={<strong>Select All</strong>}
                onClick={(v: boolean) => {
                  onSelectAll?.(v);
                }}
              />
            </li>
          )}
          {options.map((option, index) => (
            <li key={index} className={`px-2 py-1 text-left ${itemClassName}`}>
              {!("route" in option || "href" in option) && (
                <>
                  {asCheckbox ? (
                    <Checkbox
                      checked={option.checked}
                      className="w-full block"
                      disabled={option.disabled}
                      label={option.label ?? ""}
                      value={option.value ?? ""}
                      onClick={(_v: boolean) => {
                        if (onSelect && option.value) {
                          onSelect(option.value);
                        }
                      }}
                    />
                  ) : (
                    <button
                      className={`w-full ${onSelect ? "cursor-pointer" : ""}`}
                      type="button"
                      onClick={() => {
                        if (!onSelect) {
                          return;
                        }
                        if (onSelect && option.value) {
                          onSelect(option.value);
                        }
                      }}
                    >
                      {option.label}
                    </button>
                  )}
                </>
              )}
              {"route" in option && (
                <Link className="flex" to={option.route}>
                  {option.label}
                </Link>
              )}
              {"href" in option && (
                <a
                  className="flex"
                  href={option.href}
                  rel={
                    option.target === "_blank"
                      ? "noopener noreferrer"
                      : undefined
                  }
                  target={option.target}
                >
                  {option.label}
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
